import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { Interval, isAfter, isBefore, startOfHour } from 'date-fns';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningIcon } from '../../../../assets/icons';

export interface OutageSliceProps {
  interval: Interval;
  lengthInHours: number;
  isOutage: boolean;
  dayWidth: string;
  legendStatus: { showAvailable: boolean; showOutage: boolean };
  isBeingRevoked: boolean;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
}

export function OutageSlice({
  interval,
  lengthInHours,
  isOutage,
  dayWidth,
  legendStatus,
  isBeingRevoked,
  setRevokeDowntimeInterval,
}: Readonly<OutageSliceProps>): ReactElement<OutageSliceProps> {
  const [t] = useTranslation();

  const revokeDowntime = () => {
    if (!isOutage || !isAfter(interval.end, startOfHour(new Date()))) return;

    const start = isBefore(interval.start, startOfHour(new Date())) ? startOfHour(new Date()) : interval.start;

    setRevokeDowntimeInterval({ start, end: interval.end });
  };

  return (
    <Tooltip title={isOutage ? t('MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS', { ...interval }) : ''}>
      <button
        data-testid="outage-slice"
        className={clsx(
          'box-content flex h-8 appearance-none flex-wrap items-center justify-center overflow-hidden border-none p-0 transition-opacity first:grow first:pl-4',
          isOutage ? 'bg-red-200/50' : 'bg-green-200/50',
          {
            'invisible opacity-0': (isOutage && !legendStatus.showOutage) || (!isOutage && !legendStatus.showAvailable),
            'cursor-pointer hover:bg-red-200/60 active:bg-red-200/70':
              isOutage && isAfter(interval.end, startOfHour(new Date())),
            'hover:bg-green-200/60 active:bg-green-200/70': !isOutage && isAfter(interval.end, startOfHour(new Date())),
            'animate-pulse scale-y-[0.25] transition-transform': isBeingRevoked,
          },
        )}
        style={{
          minWidth: `calc(${dayWidth} / 24 * ${lengthInHours})`,
          width: `calc(100vw / 28 / 24 * ${lengthInHours})`,
        }}
        aria-label={
          isOutage
            ? t('MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS_ARIA', { ...interval })
            : t('MARKET_LOCATION_AVAILABILITY_DETAILS', { ...interval })
        }
        onClick={revokeDowntime}
      >
        {isOutage && !isBeingRevoked && lengthInHours >= 12 && (
          <div
            className="inline-flex flex-wrap items-center justify-center gap-x-1 text-xs leading-[calc(2rem-1px)]"
            aria-hidden
          >
            <WarningIcon className="my-[0.3125rem] aspect-square w-5 shrink-0" />
            {t('MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS', { ...interval })}
          </div>
        )}
      </button>
    </Tooltip>
  );
}
